import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { diffLines } from 'diff';
import { FlaskConical, Play, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { JsonView } from '@/components/JsonView';
import { Badge, Button, Card, Empty, ErrorNote, Field, Loading, Select } from '@/components/ui';
import { api, apiStream } from '@/lib/api';
import { cn } from '@/lib/cn';
import { fmtMs, fmtUsd, fmtWhen } from '@/lib/format';
import { canEdit, rk, useMe } from '@/lib/hooks';
import { getRegion, streamEndpoint } from '@/lib/regions';
import type { AgentDetail, TestCase, TestResult, TestRun } from '@/lib/types';

const TONE = { same: 'ok', changed: 'warn', error: 'bad' } as const;
const pretty = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v ?? null, null, 2));

export const AgentTests = ({ detail }: { detail: AgentDetail }) => {
  const me = useMe();
  const editable = canEdit(me.data);
  const qc = useQueryClient();
  const agentId = detail.agent.id;
  const cases = useQuery({ queryKey: rk('test-cases', agentId), queryFn: () => api<{ cases: TestCase[] }>(`/v1/agents/${agentId}/test-cases`) });
  const runs = useQuery({ queryKey: rk('test-runs', agentId), queryFn: () => api<{ runs: TestRun[] }>(`/v1/agents/${agentId}/test-runs`) });
  const draft = detail.versions.find((v) => v.status === 'draft');
  const [versionId, setVersionId] = useState(draft?.id ?? detail.agent.publishedVersionId ?? detail.versions[0]?.id ?? '');
  const [progress, setProgress] = useState<Array<{ caseId: string; name: string; status: 'same' | 'changed' | 'error'; durationMs: number }> | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const url = streamEndpoint(getRegion(), '/v1/test-runs/stream');

  const run = useMutation({
    mutationFn: async () => {
      setProgress([]);
      let runId: string | null = null;
      let failure: string | null = null;
      await apiStream(url!, { agentId, versionId }, (e) => {
        if (e.type === 'start') runId = e.runId;
        else if (e.type === 'case') setProgress((p) => [...(p ?? []), e as never]);
        else if (e.type === 'error') failure = e.message;
      });
      if (failure) throw new Error(failure);
      return runId;
    },
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: rk('test-runs', agentId) });
      setOpenRun(id);
      setProgress(null);
    },
    onError: () => setProgress(null),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/v1/test-cases/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rk('test-cases', agentId) }),
  });

  if (cases.isLoading) return <Loading />;
  const list = cases.data?.cases ?? [];

  return (
    <div className="grid gap-5 xl:grid-cols-[400px_minmax(0,1fr)]">
      <div className="flex flex-col gap-5">
        <Card title={`Test cases (${list.length})`}>
          {list.length === 0 ? (
            <Empty title="No test cases yet">
              Open a request in the log and choose <b>Save as test case</b>. Its inputs are replayed against any version, and its original output is the baseline to compare with.
            </Empty>
          ) : (
            <ul>
              {list.map((c) => (
                <li key={c.id} className="flex items-center gap-2 border-b border-line-2 px-4 py-2 last:border-0">
                  <FlaskConical className="size-3.5 shrink-0 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{c.name}</div>
                    <div className="text-xs text-ink-3">
                      {c.sourceTraceId ? (
                        <Link to="/requests/$traceId" params={{ traceId: c.sourceTraceId }} className="hover:underline">
                          from a request
                        </Link>
                      ) : (
                        'added manually'
                      )}{' '}
                      · {fmtWhen(c.createdAt)}
                      {c.baselineOutput === null && ' · no baseline'}
                    </div>
                  </div>
                  {editable && (
                    <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => confirm(`Delete "${c.name}"?`) && del.mutate(c.id)}>
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        {list.length > 0 && editable && (
          <Card title="Run the set">
            <div className="flex flex-col gap-3 p-4">
              <Field label="Against version">
                <Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                  {detail.versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.version} {v.status === 'draft' ? '(draft)' : v.id === detail.agent.publishedVersionId ? '(live)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button variant="primary" disabled={!url} loading={run.isPending} onClick={() => run.mutate()}>
                <Play className="size-3.5" /> Run {Math.min(list.length, 25)} case{list.length === 1 ? '' : 's'}
              </Button>
              {!url && <p className="text-xs text-warn">Test runs need this region’s streaming URL (streamUrl in the region config).</p>}
              <p className="text-xs text-ink-3">Calls the real model; code tools are stubbed. “Changed” means the output differs from the baseline — review it, it isn’t necessarily worse.</p>
              {run.error ? <ErrorNote error={run.error} /> : null}
              {progress && (
                <ul className="flex flex-col gap-1">
                  {progress.map((p) => (
                    <li key={p.caseId} className="flex items-center gap-2 text-xs">
                      <Badge tone={TONE[p.status]}>{p.status}</Badge> {p.name} <span className="text-ink-3">{fmtMs(p.durationMs)}</span>
                    </li>
                  ))}
                  <li className="text-xs text-ink-3">Running… {progress.length}/{Math.min(list.length, 25)}</li>
                </ul>
              )}
            </div>
          </Card>
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-5">
        <Card title="Runs">
          {!runs.data?.runs.length ? (
            <p className="px-4 py-3 text-[13px] text-ink-3">No runs yet.</p>
          ) : (
            <ul>
              {runs.data.runs.map((r) => (
                <li key={r.id}>
                  <button onClick={() => setOpenRun(openRun === r.id ? null : r.id)} className={cn('flex w-full flex-wrap items-center gap-2 border-b border-line-2 px-4 py-2 text-left text-[13px] hover:bg-line-2/50', openRun === r.id && 'bg-brand-soft')}>
                    <span className="font-medium">v{r.version}</span>
                    <Badge tone="ok">{r.passed} same</Badge>
                    {r.changed > 0 && <Badge tone="warn">{r.changed} changed</Badge>}
                    {r.failed > 0 && <Badge tone="bad">{r.failed} failed</Badge>}
                    {r.status !== 'done' && <Badge>{r.status}</Badge>}
                    <span className="ml-auto text-xs text-ink-3">
                      {r.createdBy} · {fmtWhen(r.startedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {openRun && <RunResults runId={openRun} />}
      </div>
    </div>
  );
};

const RunResults = ({ runId }: { runId: string }) => {
  const q = useQuery({ queryKey: rk('test-run', runId), queryFn: () => api<{ run: TestRun; results: TestResult[] }>(`/v1/test-runs/${runId}`) });
  const [open, setOpen] = useState<string | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  return (
    <Card title={`Results — v${q.data!.run.version}`}>
      <ul>
        {q.data!.results.map((r) => (
          <li key={r.caseId} className="border-b border-line-2 last:border-0">
            <button className="flex w-full flex-wrap items-center gap-2 px-4 py-2 text-left text-[13px] hover:bg-line-2/50" onClick={() => setOpen(open === r.caseId ? null : r.caseId)}>
              <Badge tone={TONE[r.status]}>{r.status}</Badge>
              <span className="font-medium">{r.name ?? 'deleted case'}</span>
              <span className="ml-auto text-xs text-ink-3 tabular-nums">
                {fmtMs(r.durationMs)} · {r.inputTokens ?? '—'}/{r.outputTokens ?? '—'} tok · {fmtUsd(r.costUsd)}
              </span>
            </button>
            {open === r.caseId && (
              <div className="flex flex-col gap-3 px-4 pb-4">
                {r.error && <ErrorNote error={new Error(r.error)} />}
                {r.status === 'changed' ? (
                  <div>
                    <div className="mb-1 text-xs font-medium text-ink-2">Baseline → this version</div>
                    <pre className="max-h-[420px] overflow-auto rounded-md border border-line text-[12px] leading-relaxed">
                      {diffLines(pretty(r.baseline), pretty(r.output)).map((p, i) => (
                        <span key={i} className={cn('block px-3 whitespace-pre-wrap', p.added && 'bg-ok-soft text-ok', p.removed && 'bg-bad-soft text-bad line-through', !p.added && !p.removed && 'text-ink-3')}>
                          {p.value}
                        </span>
                      ))}
                    </pre>
                  </div>
                ) : (
                  r.output !== null && <JsonView value={r.output} />
                )}
                {r.traceId && (
                  <Link to="/requests/$traceId" params={{ traceId: r.traceId }} className="text-xs text-ink-2 hover:text-ink">
                    Open trace →
                  </Link>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
};

/** Button for the trace page: save this request's inputs + output as a test case. */
export const SaveAsTestCase = ({ traceId, defaultName }: { traceId: string; defaultName: string }) => {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => api<{ case: TestCase }>('/v1/test-cases', { body: { traceId, name: prompt('Name this test case', defaultName) || defaultName } }),
    onSuccess: (r) => {
      toast.success('Saved — run it from the agent’s Tests tab');
      void qc.invalidateQueries({ queryKey: rk('test-cases', r.case.agentId) });
    },
    onError: (e) => toast.error((e as Error).message),
  });
  return (
    <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate()}>
      <FlaskConical className="size-4" /> Save as test case
    </Button>
  );
};
